/**
 * Cross-foundation-system overlap resolution on intermediate profile data.
 */
import type {
  GeneratedIntermediateFoundationSubSystem,
  IntermediateFoundationExtrusionDescriptor,
  IntermediateFoundationSection,
} from "building-types/foundations/foundationTypes";
import type { RWTable } from "common/data/table";
import { DIST_EPSILON_SQUARED } from "geom/core";
import { polygonSubtract, polygonUnion } from "geom/polyclip/polyClip";
// polygonIntersectionMulti intersects two multipolygons (polygon-clipping). geom/polyclip's
// polygonIntersection is n-way intersection of all operands, not (union of A) ∩ (union of B), so it
// is wrong when either side has multiple disjoint polygons after subtractProfiles splits profiles.
import { polygonIntersectionMulti } from "geom/polygon";
import { polyLine2Area, polyLine2SetCCW, polyLine2SetCW, polyLine2sAreCoincident } from "geom/polyLine2";
import { polyLine2AABB2 } from "geom/polyLine2";
import { polygonClose, polygonCopy, polygonSetAreaAbs, profileAreaAbs } from "geom/polyLine2Polygon";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import RBush from "rbush";

export interface SourceRef {
  readonly systemId: string;
  readonly stripHalfEdgeIndex: number;
  readonly sectionIndex: number;
  readonly extrusionIndex: number;
}

export interface OverlapProfileRecord {
  readonly id: string;
  readonly systemId: string;
  readonly foundationArea: number;
  readonly zMin: number;
  readonly zMax: number;
  readonly zBandIndex: number;
  readonly sourceRef: SourceRef;
  readonly sourceDescriptor: IntermediateFoundationExtrusionDescriptor;
  polygons: PolyLine2Polygon[];
  planGeometryModifiedByOverlap: boolean;
}

/**
 * Holes may appear in any order between equivalent profiles; match them in a bijection using the same
 * coincident test as the outer loop.
 */
/** Assign each hole in `holesA` to a distinct coincident hole in `holesB` (order-independent). */
function matchHoleLoopsBijection(holesA: PolyLine2[], holesB: PolyLine2[], nextA: number, usedB: boolean[]): boolean {
  if (nextA >= holesA.length) {
    return true;
  }
  const loopA = holesA[nextA];
  if (!loopA) {
    return false;
  }
  for (let j = 0; j < holesB.length; j++) {
    if (usedB[j]) {
      continue;
    }
    const loopB = holesB[j];
    if (!loopB) {
      continue;
    }
    if (polyLine2sAreCoincident(loopA, loopB)) {
      usedB[j] = true;
      if (matchHoleLoopsBijection(holesA, holesB, nextA + 1, usedB)) {
        return true;
      }
      usedB[j] = false;
    }
  }
  return false;
}

/** True when outer loops are coincident and hole loops match in some bijection (see `polyLine2sAreCoincident`). */
export function arePolygonsEquivalent(a: PolyLine2Polygon, b: PolyLine2Polygon): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const outerA = a[0];
  const outerB = b[0];
  if (!outerA || !outerB) {
    return false;
  }
  if (!polyLine2sAreCoincident(outerA, outerB)) {
    return false;
  }
  if (a.length === 1) {
    return true;
  }
  const holesA = a.slice(1);
  const holesB = b.slice(1);
  return matchHoleLoopsBijection(holesA, holesB, 0, new Array<boolean>(holesB.length).fill(false));
}

export function arePolygonSetsEquivalent(a: PolyLine2Polygon[], b: PolyLine2Polygon[]): boolean {
  const sortedA = sortPolygonsDeterministically(a);
  const sortedB = sortPolygonsDeterministically(b);
  if (sortedA.length !== sortedB.length) {
    return false;
  }
  for (let i = 0; i < sortedA.length; i++) {
    const loopsA = sortedA[i];
    const loopsB = sortedB[i];
    if (!loopsA || !loopsB || !arePolygonsEquivalent(loopsA, loopsB)) {
      return false;
    }
  }
  return true;
}

/** World-Z span of the extrusion; sign of `frameInfo.sourceDirZ` matches foundationStrip extrusion (`top` to `top ± depth`). */
export function descriptorZRange(descriptor: IntermediateFoundationExtrusionDescriptor): {
  zMin: number;
  zMax: number;
} {
  const originZ = descriptor.top;
  const dirZ = descriptor.frameInfo.sourceDirZ < 0 ? -descriptor.depth : descriptor.depth;
  const extentZ = originZ + dirZ;
  return {
    zMin: Math.min(originZ, extentZ),
    zMax: Math.max(originZ, extentZ),
  };
}

export function zRangesOverlap(a: OverlapProfileRecord, b: OverlapProfileRecord): boolean {
  // Half-open interval overlap: touching boundaries do not overlap.
  return !(a.zMax <= b.zMin || a.zMin >= b.zMax);
}

export function intersectionAreaAbs(aProfiles: PolyLine2Polygon[], bProfiles: PolyLine2Polygon[]): number {
  if (aProfiles.length === 0 || bProfiles.length === 0) {
    return 0;
  }
  const intersections = polygonIntersectionMulti([aProfiles, bProfiles]);
  return polygonSetAreaAbs(intersections);
}

export function subtractProfiles(
  baseProfiles: PolyLine2Polygon[],
  subtractingProfiles: PolyLine2Polygon[],
): PolyLine2Polygon[] {
  const result: PolyLine2Polygon[] = [];
  for (const base of baseProfiles) {
    const clipped = polygonSubtract({ pls: [base, ...subtractingProfiles] });
    for (const profile of clipped) {
      if (profileAreaAbs(profile) > DIST_EPSILON_SQUARED) {
        result.push(profile);
      }
    }
  }
  return result;
}

export function sortPolygonsDeterministically(polygons: PolyLine2Polygon[]): PolyLine2Polygon[] {
  return [...polygons].sort((a, b) => {
    const areaA = profileAreaAbs(a);
    const areaB = profileAreaAbs(b);
    if (Math.abs(areaA - areaB) > DIST_EPSILON_SQUARED) {
      return areaB - areaA;
    }
    // Equal area
    const aabbA = a[0] ? polyLine2AABB2(a[0]) : undefined;
    const aabbB = b[0] ? polyLine2AABB2(b[0]) : undefined;
    if (!aabbA || !aabbB) {
      return 0;
    }
    if (aabbA.minX !== aabbB.minX) {
      return aabbA.minX - aabbB.minX;
    }
    if (aabbA.minY !== aabbB.minY) {
      return aabbA.minY - aabbB.minY;
    }
    if (aabbA.maxX !== aabbB.maxX) {
      return aabbA.maxX - aabbB.maxX;
    }
    return aabbA.maxY - aabbB.maxY;
  });
}

// Only used as a fallback for deterministically sorting polygons with equal area.
export function comparePolygonCoordsLexicographically(a: PolyLine2Polygon[], b: PolyLine2Polygon[]): number {
  const sortedA = sortPolygonsDeterministically(a);
  const sortedB = sortPolygonsDeterministically(b);
  const minPolygonCount = Math.min(sortedA.length, sortedB.length);
  for (let i = 0; i < minPolygonCount; i++) {
    const loopsA = sortedA[i];
    const loopsB = sortedB[i];
    const minLoopCount = Math.min(loopsA.length, loopsB.length);
    for (let j = 0; j < minLoopCount; j++) {
      const loopA = loopsA[j];
      const loopB = loopsB[j];
      const minCoordCount = Math.min(loopA.length, loopB.length);
      for (let k = 0; k < minCoordCount; k++) {
        if (loopA[k] !== loopB[k]) {
          return loopA[k] - loopB[k];
        }
      }
      if (loopA.length !== loopB.length) {
        return loopA.length - loopB.length;
      }
    }
    if (loopsA.length !== loopsB.length) {
      return loopsA.length - loopsB.length;
    }
  }
  return sortedA.length - sortedB.length;
}

// Smaller parent foundation perimeter area should be subtracted.
// Equal-area ties use the profile-area heuristic, then lexicographic polygon coordinates.
export function chooseLoserByFoundationArea(
  a: OverlapProfileRecord,
  b: OverlapProfileRecord,
): OverlapProfileRecord | undefined {
  if (Math.abs(a.foundationArea - b.foundationArea) >= DIST_EPSILON_SQUARED) {
    return a.foundationArea < b.foundationArea ? a : b;
  }
  const profileAreaA = polygonSetAreaAbs(a.polygons);
  const profileAreaB = polygonSetAreaAbs(b.polygons);
  if (profileAreaA <= DIST_EPSILON_SQUARED && profileAreaB <= DIST_EPSILON_SQUARED) {
    return undefined;
  }
  if (Math.abs(profileAreaA - profileAreaB) < DIST_EPSILON_SQUARED) {
    return comparePolygonCoordsLexicographically(a.polygons, b.polygons) > 0 ? a : b;
  }
  return profileAreaA < profileAreaB ? a : b;
}

export function enumerateCandidatePairs(records: Map<string, OverlapProfileRecord>): Array<[string, string]> {
  interface ProfileAABBItem {
    readonly minX: number;
    readonly minY: number;
    readonly maxX: number;
    readonly maxY: number;
    readonly recordId: string;
    readonly systemId: string;
  }

  const rbushItems: ProfileAABBItem[] = [];
  for (const [recordId, record] of records) {
    for (const polygon of record.polygons) {
      const outer = polygon[0];
      if (!outer) {
        continue;
      }
      const aabb = polyLine2AABB2(outer);
      rbushItems.push({
        ...aabb,
        recordId,
        systemId: record.systemId,
      });
    }
  }
  if (rbushItems.length === 0) {
    return [];
  }

  const rtree = new RBush<ProfileAABBItem>();
  rtree.load(rbushItems);

  const pairSet = new Set<string>();
  const pairSeparator = "\u0000";
  for (const item of rbushItems) {
    const neighbors = rtree.search(item);
    for (const neighbor of neighbors) {
      if (item.recordId === neighbor.recordId) {
        continue;
      }
      if (item.systemId === neighbor.systemId) {
        continue;
      }
      const low = item.recordId < neighbor.recordId ? item.recordId : neighbor.recordId;
      const high = item.recordId < neighbor.recordId ? neighbor.recordId : item.recordId;
      pairSet.add(`${low}${pairSeparator}${high}`);
    }
  }
  return [...pairSet]
    .map((key) => key.split(pairSeparator))
    .filter((parts): parts is [string, string] => parts.length === 2)
    .sort((a, b) => {
      if (a[0] !== b[0]) {
        return a[0].localeCompare(b[0]);
      }
      return a[1].localeCompare(b[1]);
    });
}

function buildProfileRecords(
  foundations: RWTable<GeneratedIntermediateFoundationSubSystem>,
): Map<string, OverlapProfileRecord> {
  interface RawProfileRecord {
    readonly id: string;
    readonly systemId: string;
    readonly zMin: number;
    readonly zMax: number;
    readonly sourceDescriptor: IntermediateFoundationExtrusionDescriptor;
    readonly sourceRef: SourceRef;
    readonly polygon: PolyLine2Polygon;
  }

  const records = new Map<string, OverlapProfileRecord>();
  const rawRecords: RawProfileRecord[] = [];
  const zBreaks: number[] = [];
  const foundationAreaBySystem = new Map<string, number>();
  const maxProfileAreaBySystem = new Map<string, number>();
  const planProfilesBySystem = new Map<string, PolyLine2Polygon[]>();

  function collectForSystem(
    systemId: string,
    strips: GeneratedIntermediateFoundationSubSystem["subSystem"]["strips"],
  ): void {
    for (const [stripKey, strip] of Object.entries(strips)) {
      const stripHalfEdgeIndex = Number(stripKey);
      for (let sectionIndex = 0; sectionIndex < strip.sections.length; sectionIndex++) {
        const section = strip.sections[sectionIndex];
        for (let i = 0; i < section.extrusionDescriptors.length; i++) {
          const descriptor = section.extrusionDescriptors[i];
          const loops = descriptor.profile;
          if (loops.length === 0) {
            continue;
          }
          const polygon: PolyLine2Polygon = loops;
          if (profileAreaAbs(polygon) <= DIST_EPSILON_SQUARED) {
            continue;
          }
          const { zMin, zMax } = descriptorZRange(descriptor);
          if (zMax - zMin <= DIST_EPSILON_SQUARED) {
            continue;
          }
          const id = `${systemId}::${stripHalfEdgeIndex}::${sectionIndex}::${i}`;
          rawRecords.push({
            id,
            systemId,
            zMin,
            zMax,
            sourceDescriptor: descriptor,
            sourceRef: {
              systemId,
              stripHalfEdgeIndex,
              sectionIndex,
              extrusionIndex: i,
            },
            polygon,
          });
          const polygonAreaAbs = profileAreaAbs(polygon);
          const currentMax = maxProfileAreaBySystem.get(systemId) ?? 0;
          if (polygonAreaAbs > currentMax) {
            maxProfileAreaBySystem.set(systemId, polygonAreaAbs);
          }
          if (descriptor.includeInPlanProfiles) {
            const existingPlanProfiles = planProfilesBySystem.get(systemId);
            if (existingPlanProfiles) {
              existingPlanProfiles.push(polygon);
            } else {
              planProfilesBySystem.set(systemId, [polygon]);
            }
          }
          zBreaks.push(zMin, zMax);
        }
      }
    }
  }
  for (const [systemId, foundation] of Object.entries(foundations)) {
    const perimeterAreaAbs = foundation.perimeter ? Math.abs(polyLine2Area(foundation.perimeter)) : 0;
    foundationAreaBySystem.set(systemId, perimeterAreaAbs);
    collectForSystem(systemId, foundation.subSystem.strips);
  }
  for (const systemId of Object.keys(foundations)) {
    const perimeterAreaAbs = foundationAreaBySystem.get(systemId) ?? 0;
    const systemPlanProfiles = planProfilesBySystem.get(systemId) ?? [];
    const unionedPlanProfiles = systemPlanProfiles.length > 0 ? polygonUnion({ pls: systemPlanProfiles }) : [];
    const planFootprintAreaAbs = polygonSetAreaAbs(unionedPlanProfiles);
    const profileAreaAbsMax = maxProfileAreaBySystem.get(systemId) ?? 0;
    // Prefer explicit perimeter area when available. For room-based systems that do not
    // carry a perimeter, fall back to footprint area from the union of all plan profiles.
    const perimeterPreferredArea = perimeterAreaAbs > DIST_EPSILON_SQUARED ? perimeterAreaAbs : planFootprintAreaAbs;
    const effectiveArea = Math.max(perimeterPreferredArea, profileAreaAbsMax);
    foundationAreaBySystem.set(systemId, effectiveArea);
  }

  const sortedBreaks = [...new Set(zBreaks)].sort((a, b) => a - b);
  if (sortedBreaks.length < 2) {
    return records;
  }
  for (const raw of rawRecords) {
    for (let bandIndex = 0; bandIndex < sortedBreaks.length - 1; bandIndex++) {
      const bandMin = sortedBreaks[bandIndex];
      const bandMax = sortedBreaks[bandIndex + 1];
      if (bandMax - bandMin <= DIST_EPSILON_SQUARED) {
        continue;
      }
      if (bandMax <= raw.zMin || bandMin >= raw.zMax) {
        continue;
      }
      const bandId = `${raw.id}::z${bandIndex}`;
      records.set(bandId, {
        id: bandId,
        systemId: raw.systemId,
        foundationArea: foundationAreaBySystem.get(raw.systemId) ?? 0,
        zMin: bandMin,
        zMax: bandMax,
        zBandIndex: bandIndex,
        sourceDescriptor: raw.sourceDescriptor,
        sourceRef: raw.sourceRef,
        polygons: [raw.polygon],
        planGeometryModifiedByOverlap: false,
      });
    }
  }

  return records;
}

/** Max overlap-resolution sweeps before giving up; iteration stops early when a pass makes no change. */
export const RESOLVE_OVERLAPS_MAX_PASSES = 6; // Reasonable arbitrary cap (not derived from a fixed-point proof).
// Overlap checks must be looser than DIST_EPSILON_SQUARED because polygon subtraction rounds/snaps
// geometry at a much coarser precision and can leave tiny residual slivers.
const OVERLAP_AREA_TOL = 1e-3;

function resolveOverlaps(records: Map<string, OverlapProfileRecord>): void {
  // Iterate to a fixed point so order-dependent cuts cannot leave residual overlaps.
  for (let pass = 0; pass < RESOLVE_OVERLAPS_MAX_PASSES; pass++) {
    let changed = false;
    const pairs = enumerateCandidatePairs(records);
    for (const [aId, bId] of pairs) {
      const a = records.get(aId);
      const b = records.get(bId);
      if (!a || !b) {
        continue;
      }
      if (!zRangesOverlap(a, b)) {
        continue;
      }
      if (a.polygons.length === 0 || b.polygons.length === 0) {
        continue;
      }
      const overlapArea = intersectionAreaAbs(a.polygons, b.polygons);
      if (overlapArea <= OVERLAP_AREA_TOL) {
        continue;
      }
      const loser = chooseLoserByFoundationArea(a, b);
      if (!loser) {
        continue;
      }
      const winner = loser === a ? b : a;
      const updatedPolygons = subtractProfiles(loser.polygons, winner.polygons);
      if (!arePolygonSetsEquivalent(loser.polygons, updatedPolygons)) {
        loser.polygons = updatedPolygons;
        loser.planGeometryModifiedByOverlap = true;
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
  }
  console.warn(`foundation overlap resolution did not converge within ${RESOLVE_OVERLAPS_MAX_PASSES} passes`);
}

export function outerLoop(polygon: PolyLine2Polygon): PolyLine2 | undefined {
  const outer = polygon[0];
  return outer;
}

export interface OverlapProfileDescriptorRun {
  readonly sourceDescriptor: IntermediateFoundationExtrusionDescriptor;
  readonly polygons: PolyLine2Polygon[];
  readonly zMin: number;
  readonly zMax: number;
}

export function buildDescriptorRunsForEmission(sortedRecs: OverlapProfileRecord[]): OverlapProfileDescriptorRun[] {
  const runs: OverlapProfileDescriptorRun[] = [];
  let recIdx = 0;
  while (recIdx < sortedRecs.length) {
    const rec = sortedRecs[recIdx];
    if (!rec) {
      recIdx++;
      continue;
    }
    if (rec.planGeometryModifiedByOverlap) {
      recIdx++;
      while (recIdx < sortedRecs.length) {
        const next = sortedRecs[recIdx];
        if (!next || !next.planGeometryModifiedByOverlap) {
          break;
        }
        if (!arePolygonSetsEquivalent(rec.polygons, next.polygons)) {
          break;
        }
        recIdx++;
      }
      const runLast = sortedRecs[recIdx - 1];
      if (!runLast) {
        continue;
      }
      runs.push({
        sourceDescriptor: rec.sourceDescriptor,
        polygons: rec.polygons,
        zMin: rec.zMin,
        zMax: runLast.zMax,
      });
      continue;
    }
    const runStart = recIdx;
    while (recIdx < sortedRecs.length) {
      const r = sortedRecs[recIdx];
      if (!r || r.planGeometryModifiedByOverlap) {
        break;
      }
      recIdx++;
    }
    const runFirst = sortedRecs[runStart];
    const runLast = sortedRecs[recIdx - 1];
    if (!runFirst || !runLast) {
      continue;
    }
    runs.push({
      sourceDescriptor: runFirst.sourceDescriptor,
      polygons: runFirst.polygons,
      zMin: runFirst.zMin,
      zMax: runLast.zMax,
    });
  }
  return runs;
}

function descriptorFromPolygon(
  source: IntermediateFoundationExtrusionDescriptor,
  polygon: PolyLine2Polygon,
  zMin: number,
  zMax: number,
): IntermediateFoundationExtrusionDescriptor {
  const normalizedPolygon = arePolygonsEquivalent(polygon, source.profile)
    ? polygonClose(polygonCopy(source.profile)).polygon
    : polygonClose(polygonCopy(polygon)).polygon;
  const normalizedOuter = normalizedPolygon[0];
  if (normalizedOuter) {
    polyLine2SetCW(normalizedOuter);
    for (let i = 1; i < normalizedPolygon.length; i++) {
      const normalizedLoop = normalizedPolygon[i];
      if (!normalizedLoop) {
        continue;
      }
      polyLine2SetCCW(normalizedLoop);
    }
  }
  const sourceDirZ = source.frameInfo.sourceDirZ < 0 ? -source.depth : source.depth;
  const sourceExtent = source.top + sourceDirZ;
  const sourceTopIsUpper = source.top >= sourceExtent;
  const bandDepth = zMax - zMin;
  return {
    ...source,
    top: sourceTopIsUpper ? zMax : zMin,
    depth: sourceTopIsUpper ? bandDepth : -bandDepth,
    profile: normalizedPolygon,
  };
}

function applyResolvedProfilesToFoundations(
  records: Map<string, OverlapProfileRecord>,
  foundations: RWTable<GeneratedIntermediateFoundationSubSystem>,
): RWTable<GeneratedIntermediateFoundationSubSystem> {
  const bySection = new Map<string, OverlapProfileRecord[]>();
  for (const record of records.values()) {
    const key = `${record.sourceRef.systemId}::${record.sourceRef.stripHalfEdgeIndex}::${record.sourceRef.sectionIndex}`;
    if (!bySection.has(key)) {
      bySection.set(key, []);
    }
    const sectionRecords = bySection.get(key);
    if (sectionRecords) {
      sectionRecords.push(record);
    }
  }

  function rebuildSection(
    section: IntermediateFoundationSection,
    sectionRecords: OverlapProfileRecord[],
  ): IntermediateFoundationSection {
    const recByDescriptorIndex = new Map<number, OverlapProfileRecord[]>();
    for (const record of sectionRecords) {
      const recs = recByDescriptorIndex.get(record.sourceRef.extrusionIndex);
      if (recs) {
        recs.push(record);
      } else {
        recByDescriptorIndex.set(record.sourceRef.extrusionIndex, [record]);
      }
    }
    const newPlanProfiles: PolyLine2[] = [];
    const newDescriptors: IntermediateFoundationExtrusionDescriptor[] = [];

    for (let i = 0; i < section.extrusionDescriptors.length; i++) {
      const recsForDescriptor = recByDescriptorIndex.get(i);
      if (!recsForDescriptor || recsForDescriptor.length === 0) {
        const descriptor = section.extrusionDescriptors[i];
        newDescriptors.push(descriptor);
        if (descriptor.includeInPlanProfiles) {
          const outer = outerLoop(descriptor.profile);
          if (outer) {
            newPlanProfiles.push(outer);
          }
        }
        continue;
      }
      const sortedRecs = [...recsForDescriptor].sort((a, b) => {
        if (a.zBandIndex !== b.zBandIndex) {
          return a.zBandIndex - b.zBandIndex;
        }
        if (a.zMin !== b.zMin) {
          return a.zMin - b.zMin;
        }
        if (a.zMax !== b.zMax) {
          return a.zMax - b.zMax;
        }
        return a.id.localeCompare(b.id);
      });

      const pushDescriptorAndPlan = (
        source: IntermediateFoundationExtrusionDescriptor,
        polygon: PolyLine2Polygon,
        zMin: number,
        zMax: number,
      ): void => {
        if (profileAreaAbs(polygon) <= DIST_EPSILON_SQUARED) {
          return;
        }
        const descriptor = descriptorFromPolygon(source, polygon, zMin, zMax);
        newDescriptors.push(descriptor);
        if (descriptor.includeInPlanProfiles) {
          const outer = outerLoop(descriptor.profile);
          if (outer) {
            newPlanProfiles.push(outer);
          }
        }
      };

      const runs = buildDescriptorRunsForEmission(sortedRecs);
      for (const run of runs) {
        const runPolygons = sortPolygonsDeterministically(run.polygons);
        for (const polygon of runPolygons) {
          pushDescriptorAndPlan(run.sourceDescriptor, polygon, run.zMin, run.zMax);
        }
      }
    }

    if (newPlanProfiles.length === 0) {
      for (const descriptor of newDescriptors) {
        const outer = outerLoop(descriptor.profile);
        if (outer) {
          newPlanProfiles.push(outer);
        }
      }
    }
    // If every descriptor was clipped away, `newDescriptors` is empty — do not restore
    // pre-resolution outlines (that would ghost a fully consumed section on the plan).
    if (newPlanProfiles.length === 0 && newDescriptors.length > 0) {
      for (const descriptor of section.extrusionDescriptors) {
        const outer = outerLoop(descriptor.profile);
        if (outer) {
          newPlanProfiles.push(outer);
        }
      }
    }

    return {
      ...section,
      planProfiles: newPlanProfiles,
      extrusionDescriptors: newDescriptors,
    };
  }

  const out: RWTable<GeneratedIntermediateFoundationSubSystem> = {};
  for (const [systemId, system] of Object.entries(foundations)) {
    const rebuiltStrips: GeneratedIntermediateFoundationSubSystem["subSystem"]["strips"] = {};
    for (const [stripKey, strip] of Object.entries(system.subSystem.strips)) {
      const stripHalfEdgeIndex = Number(stripKey);
      const rebuiltSections = strip.sections.map((section, sectionIndex) => {
        const key = `${systemId}::${stripHalfEdgeIndex}::${sectionIndex}`;
        const sectionRecords = bySection.get(key);
        if (!sectionRecords || sectionRecords.length === 0) {
          return section;
        }
        return rebuildSection(section, sectionRecords);
      });
      rebuiltStrips[Number(stripKey)] = {
        ...strip,
        sections: rebuiltSections,
      };
    }
    out[systemId] = {
      ...system,
      subSystem: {
        ...system.subSystem,
        strips: rebuiltStrips,
      },
    };
  }
  return out;
}

export function applyFoundationOverlapResolution(
  foundations: RWTable<GeneratedIntermediateFoundationSubSystem>,
): RWTable<GeneratedIntermediateFoundationSubSystem> {
  const records = buildProfileRecords(foundations);
  resolveOverlaps(records);
  return applyResolvedProfilesToFoundations(records, foundations);
}
